'use client';

/**
 * V4ErrorShell — canonical marketing-route error state.
 *
 * Client component (Next.js error boundary requirement). Drop into a route
 * segment's error.tsx:
 *
 *   'use client';
 *   import { V4ErrorShell } from '@/components/marketing/route-states/V4ErrorShell';
 *   export default function Error({ error, reset }: {
 *     error: Error & { digest?: string };
 *     reset: () => void;
 *   }) {
 *     return <V4ErrorShell pillar="cyan" error={error} reset={reset} />;
 *   }
 *
 * Renders SectLabel eyebrow + Geist-Sans heading with italic Instrument-Serif
 * accent on the pillar-tinted accent word, friendly body copy, error digest
 * if present, and a Try-again / Back-to-homepage action row.
 */

import { useEffect } from 'react';
import { SectLabel, V4CtaButton, V4GhostButton } from '@/components/landing/v4-shared';
import './route-states.css';

type Pillar = 'cyan' | 'amber' | 'jade' | 'rose' | 'paper';

const PILLAR_ACCENT: Record<Pillar, string> = {
  cyan: 'var(--cyan-glow)',
  amber: 'var(--amber-glow)',
  jade: 'var(--jade-glow)',
  rose: 'var(--rose)',
  paper: 'var(--paper)',
};

interface Props {
  pillar?: Pillar;
  eyebrow?: string;
  heading?: string;
  accent?: string;
  body?: string;
  error: Error & { digest?: string };
  reset: () => void;
}

export function V4ErrorShell({
  pillar = 'cyan',
  eyebrow = 'Something went wrong',
  heading = 'We hit a',
  accent = 'wrong',
  body = 'Try again — or head back to the homepage and we will reach you the long way around.',
  error,
  reset,
}: Props) {
  useEffect(() => {
    if (typeof console !== 'undefined') {
      console.error('[marketing route] error', error);
    }
  }, [error]);

  // V4GhostButton variant only accepts 'cyan' | 'amber' | 'jade' | 'paper';
  // collapse rose → paper for the back-home CTA so contrast stays AA.
  const ghostVariant: 'cyan' | 'amber' | 'jade' | 'paper' =
    pillar === 'rose' ? 'paper' : pillar;

  return (
    <main
      data-v4-root
      data-pillar={pillar}
      className="v4-route-state"
    >
      <div className="v4-route-state__inner">
        <SectLabel color={PILLAR_ACCENT[pillar]}>{eyebrow}</SectLabel>
        <h2 className="v4-route-state__heading">
          {heading}{' '}
          <em
            className="v4-route-state__heading-accent"
            data-pillar={pillar}
          >
            {accent}
          </em>
          {'.'}
        </h2>
        <p className="v4-route-state__body">{body}</p>
        {error.digest ? (
          <code className="v4-route-state__digest">ref: {error.digest}</code>
        ) : null}
        <div className="v4-route-state__actions">
          <V4CtaButton onClick={reset}>Try again</V4CtaButton>
          <V4GhostButton variant={ghostVariant} href="/">
            Back to homepage
          </V4GhostButton>
        </div>
      </div>
    </main>
  );
}

export default V4ErrorShell;
